import { IVendorAdapter } from './IVendorAdapter';
import { ApiResponse } from '../../types/api';
import {
  Vendor, AMCContract, CreateVendorDto, CreateAMCContractDto, VendorFilter,
  VendorPerformanceMetrics,
} from '../../types/vendor';
import { ApiClient } from '../apiClient';
import { hasApiEndpoint } from '../../config/backend';

// ─── Local demo data store (used when no API endpoint is configured) ───
const DEMO_VENDORS: Vendor[] = [
  {
    vendor_id: 'VND-2026-00001',
    name: 'Alpha Technical Services LLC',
    trade_license_no: 'TL-2024-8821',
    contact_person: 'Mohammed Al-Rashid',
    contact_email: 'mrashid@alphatech.ae',
    contact_phone: '+971-50-1234567',
    address: 'Industrial Area 4, Sharjah, UAE',
    status: 'active',
    category_mappings: [
      { category_id: 'CAT-HVAC', category_name: 'HVAC & AC Systems', sub_categories: ['Split AC', 'Chiller', 'AHU'] },
      { category_id: 'CAT-MECH', category_name: 'Mechanical', sub_categories: ['Pumps', 'Compressors'] },
    ],
    service_areas: [
      { location_id: 'LOC-001', location_name: 'Block A', zone: 'North' },
      { location_id: 'LOC-002', location_name: 'Block B', zone: 'North' },
    ],
    performance: {
      avg_response_time_hours: 2.4,
      on_time_completion_rate: 91.5,
      inspection_pass_rate: 88.0,
      cost_variance_percent: 3.2,
      total_jobs_completed: 214,
      total_jobs_assigned: 228,
      rating: 4.2,
    },
    created_at: '2026-01-15T08:00:00Z',
    updated_at: '2026-09-01T12:00:00Z',
    attachment_urls: [],
  },
  {
    vendor_id: 'VND-2026-00002',
    name: 'Beta Plumbing & Civil Works',
    trade_license_no: 'TL-2023-4412',
    contact_person: 'Rajan Nair',
    contact_email: 'rajan@betaplumbing.ae',
    contact_phone: '+971-55-9876543',
    address: 'Hamdan Street, Abu Dhabi, UAE',
    status: 'active',
    category_mappings: [
      { category_id: 'CAT-PLMB', category_name: 'Plumbing', sub_categories: ['Drainage', 'Water Lines', 'Fixtures'] },
      { category_id: 'CAT-CIVIL', category_name: 'Civil Works', sub_categories: ['Waterproofing', 'Tiling'] },
    ],
    service_areas: [
      { location_id: 'LOC-003', location_name: 'Block C', zone: 'South' },
    ],
    performance: {
      avg_response_time_hours: 3.8,
      on_time_completion_rate: 84.0,
      inspection_pass_rate: 82.5,
      cost_variance_percent: -1.5,
      total_jobs_completed: 132,
      total_jobs_assigned: 145,
      rating: 3.8,
    },
    created_at: '2026-02-20T09:00:00Z',
    updated_at: '2026-08-15T10:00:00Z',
    attachment_urls: [],
  },
];

const DEMO_CONTRACTS: AMCContract[] = [
  {
    contract_id: 'AMC-2026-00001',
    vendor_id: 'VND-2026-00001',
    vendor_name: 'Alpha Technical Services LLC',
    contract_title: 'Annual HVAC Maintenance Contract 2026',
    scope_of_work: 'Quarterly preventive maintenance of all split AC units, chillers, and AHU systems across assigned blocks.',
    covered_categories: ['CAT-HVAC', 'CAT-MECH'],
    covered_locations: ['LOC-001', 'LOC-002'],
    start_date: '2026-01-01',
    end_date: '2026-12-31',
    contract_value: 485000,
    currency: 'AED',
    status: 'active',
    sla_response_hours: 4,
    sla_completion_hours: 24,
    created_at: '2026-01-10T08:00:00Z',
    updated_at: '2026-01-10T08:00:00Z',
  },
];

function generateId(prefix: string, arr: { [key: string]: string }[]): string {
  const seq = String(arr.length + 1).padStart(5, '0');
  return `${prefix}-2026-${seq}`;
}

function ok<T>(data: T, message = 'OK'): ApiResponse<T> {
  return { success: true, data, message };
}

function applyFilter(vendors: Vendor[], filter?: VendorFilter): Vendor[] {
  let result = [...vendors];
  if (filter?.status) result = result.filter(v => v.status === filter.status);
  if (filter?.category_id) result = result.filter(v => v.category_mappings.some(c => c.category_id === filter.category_id));
  if (filter?.location_id) result = result.filter(v => v.service_areas.some(a => a.location_id === filter.location_id));
  if (filter?.search) {
    const q = filter.search.toLowerCase();
    result = result.filter(v =>
      v.name.toLowerCase().includes(q) ||
      v.contact_person.toLowerCase().includes(q) ||
      v.vendor_id.toLowerCase().includes(q)
    );
  }
  return result;
}

const emptyPerf = (): VendorPerformanceMetrics => ({
  avg_response_time_hours: 0,
  on_time_completion_rate: 0,
  inspection_pass_rate: 0,
  cost_variance_percent: 0,
  total_jobs_completed: 0,
  total_jobs_assigned: 0,
  rating: 0,
});

export class GASVendorAdapter implements IVendorAdapter {
  private vendors: Vendor[] = [...DEMO_VENDORS];
  private contracts: AMCContract[] = [...DEMO_CONTRACTS];

  private get isLive(): boolean {
    return hasApiEndpoint();
  }

  async getVendors(filter?: VendorFilter): Promise<ApiResponse<Vendor[]>> {
    if (this.isLive) {
      const params = new URLSearchParams();
      if (filter?.status) params.set('status', filter.status);
      if (filter?.category_id) params.set('category_id', filter.category_id);
      if (filter?.location_id) params.set('location_id', filter.location_id);
      if (filter?.search) params.set('search', filter.search);
      const qs = params.toString();
      return ApiClient.get<Vendor[]>(`/vendors${qs ? `?${qs}` : ''}`);
    }
    await new Promise(r => setTimeout(r, 300));
    return ok(applyFilter(this.vendors, filter), 'Loaded vendors');
  }

  async getVendorById(id: string): Promise<ApiResponse<Vendor>> {
    if (this.isLive) {
      return ApiClient.get<Vendor>(`/vendors/${id}`);
    }
    await new Promise(r => setTimeout(r, 200));
    const v = this.vendors.find(x => x.vendor_id === id);
    if (!v) return { success: false, data: null as unknown as Vendor, message: 'Vendor not found', errors: ['Vendor not found'] };
    return ok(v, 'Vendor loaded');
  }

  async createVendor(dto: CreateVendorDto): Promise<ApiResponse<Vendor>> {
    if (this.isLive) {
      return ApiClient.post<Vendor>('/vendors', dto);
    }
    await new Promise(r => setTimeout(r, 500));
    const now = new Date().toISOString();
    const vendor: Vendor = {
      ...dto,
      vendor_id: generateId('VND', this.vendors as unknown as { [key: string]: string }[]),
      status: 'pending_approval',
      performance: emptyPerf(),
      created_at: now,
      updated_at: now,
      attachment_urls: [],
    };
    this.vendors.push(vendor);
    return ok(vendor, 'Vendor created');
  }

  async updateVendor(id: string, dto: Partial<CreateVendorDto>): Promise<ApiResponse<Vendor>> {
    if (this.isLive) {
      return ApiClient.put<Vendor>(`/vendors/${id}`, dto);
    }
    await new Promise(r => setTimeout(r, 400));
    const idx = this.vendors.findIndex(v => v.vendor_id === id);
    if (idx === -1) return { success: false, data: null as unknown as Vendor, message: 'Vendor not found', errors: ['Vendor not found'] };
    this.vendors[idx] = { ...this.vendors[idx], ...dto, updated_at: new Date().toISOString() };
    return ok(this.vendors[idx], 'Vendor updated');
  }

  async updateVendorStatus(id: string, status: Vendor['status']): Promise<ApiResponse<Vendor>> {
    if (this.isLive) {
      const res = await ApiClient.post<{ vendor_id: string }>(`/vendors/${id}/status`, { status });
      if (!res.success) return res as unknown as ApiResponse<Vendor>;
      return this.getVendorById(id);
    }
    await new Promise(r => setTimeout(r, 300));
    const idx = this.vendors.findIndex(v => v.vendor_id === id);
    if (idx === -1) return { success: false, data: null as unknown as Vendor, message: 'Vendor not found', errors: ['Vendor not found'] };
    this.vendors[idx] = { ...this.vendors[idx], status, updated_at: new Date().toISOString() };
    return ok(this.vendors[idx], 'Vendor status updated');
  }

  async getContracts(vendorId?: string): Promise<ApiResponse<AMCContract[]>> {
    if (this.isLive) {
      const qs = vendorId ? `?vendor_id=${encodeURIComponent(vendorId)}` : '';
      return ApiClient.get<AMCContract[]>(`/vendors/contracts${qs}`);
    }
    await new Promise(r => setTimeout(r, 300));
    const result = vendorId ? this.contracts.filter(c => c.vendor_id === vendorId) : this.contracts;
    return ok(result, 'Loaded contracts');
  }

  async getContractById(id: string): Promise<ApiResponse<AMCContract>> {
    if (this.isLive) {
      return ApiClient.get<AMCContract>(`/vendors/contracts?id=${encodeURIComponent(id)}`);
    }
    await new Promise(r => setTimeout(r, 200));
    const c = this.contracts.find(x => x.contract_id === id);
    if (!c) return { success: false, data: null as unknown as AMCContract, message: 'Contract not found', errors: ['Contract not found'] };
    return ok(c, 'Contract loaded');
  }

  async createContract(dto: CreateAMCContractDto): Promise<ApiResponse<AMCContract>> {
    if (this.isLive) {
      return ApiClient.post<AMCContract>(`/vendors/${dto.vendor_id}/contracts`, dto);
    }
    await new Promise(r => setTimeout(r, 500));
    const now = new Date().toISOString();
    const vendor = this.vendors.find(v => v.vendor_id === dto.vendor_id);
    const contract: AMCContract = {
      ...dto,
      contract_id: generateId('AMC', this.contracts as unknown as { [key: string]: string }[]),
      vendor_name: vendor?.name ?? 'Unknown Vendor',
      status: 'pending',
      created_at: now,
      updated_at: now,
    };
    this.contracts.push(contract);
    return ok(contract, 'Contract created');
  }

  async updateContractStatus(id: string, status: AMCContract['status']): Promise<ApiResponse<AMCContract>> {
    if (this.isLive) {
      const res = await ApiClient.put<{ contract_id: string }>('/vendors/contracts', { contract_id: id, status });
      if (!res.success) return res as unknown as ApiResponse<AMCContract>;
      return this.getContractById(id);
    }
    await new Promise(r => setTimeout(r, 300));
    const idx = this.contracts.findIndex(c => c.contract_id === id);
    if (idx === -1) return { success: false, data: null as unknown as AMCContract, message: 'Contract not found', errors: ['Contract not found'] };
    this.contracts[idx] = { ...this.contracts[idx], status, updated_at: new Date().toISOString() };
    return ok(this.contracts[idx], 'Contract status updated');
  }
}
